import { ErrorResponse, type User } from "oidc-client-ts";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { mayResume, safeReturnTo, setSignedInMarker, userManager, wasSignedIn, type Realm } from "./realms";

/** The waits before each new try of a failed renewal; then the session counts as ended. */
const RENEW_RETRY_MS = [2_000, 10_000, 30_000];
/** Tokens that arrived this close before a failed renewal, or after it, came from a renewal that won a race. */
const RACE_MS = 5_000;

export type AuthStatus = "loading" | "signedOut" | "signedIn" | "expired";

export type AuthState = {
  realm: Realm;
  status: AuthStatus;
  user: User | null;
  /** fresh asks Keycloak for the password again (prompt=login), for steps that need a recent sign-in (UR-06). */
  signIn: (returnTo?: string, opts?: { fresh?: boolean }) => Promise<void>;
  signOut: () => Promise<void>;
  /** Completes the redirect back from Keycloak; resolves to the path to show next. */
  completeSignIn: () => Promise<string>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ realm, children }: { realm: Realm; children: ReactNode }) {
  const manager = userManager(realm);
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  useEffect(() => {
    let active = true;
    // When tokens last arrived: a renewal that fails right after another one succeeded lost a race, nothing ended.
    let loadedAt = 0;
    const onLoaded = (u: User) => {
      loadedAt = Date.now();
      setUser(u);
      setStatus("signedIn");
    };
    const onUnloaded = () => {
      setUser(null);
      setStatus("signedOut");
    };
    // The refresh failed for good (Keycloak session ended, idle timeout, user signed out elsewhere): WEB-F-SH-01
    // expiry. Keycloak's own session often outlives the tokens, so the tab first goes back through Keycloak by itself,
    // as after a reload, and lands on the same page without a click (owner's report #5); the session-ended page shows
    // only when that was tried a moment ago.
    const onExpired = () => {
      setUser(null);
      if (wasSignedIn(realm) && mayResume(realm)) {
        setStatus("signedOut");
        return;
      }
      setSignedInMarker(realm, false);
      setStatus("expired");
    };
    // A renewal can fail without the session having ended: woken from sleep before the network is back, or a second
    // renewal racing one that already rotated the refresh token. Those are tried again before giving up (#5).
    let retrying = false;
    const onRenewError = (first: Error) => {
      if (retrying) return;
      retrying = true;
      const failedAt = Date.now();
      void (async () => {
        try {
          let error: unknown = first;
          for (const wait of RENEW_RETRY_MS) {
            await new Promise((done) => setTimeout(done, wait));
            if (!active || loadedAt >= failedAt - RACE_MS) return; // gone, or the other renewal won
            // Keycloak itself said no (invalid_grant): trying the same token again cannot help.
            if (error instanceof ErrorResponse) break;
            if (!navigator.onLine) await new Promise((done) => window.addEventListener("online", done, { once: true }));
            if (!active) return;
            try {
              await manager.signinSilent();
              return;
            } catch (e) {
              error = e; // tried again, or given up
            }
          }
          if (active) onExpired();
        } finally {
          retrying = false;
        }
      })();
    };
    manager.events.addUserLoaded(onLoaded);
    manager.events.addUserUnloaded(onUnloaded);
    manager.events.addSilentRenewError(onRenewError);
    const onTokenExpired = () => {
      void manager.getUser().then((u) => {
        if (!u?.refresh_token) onExpired();
      });
    };
    manager.events.addAccessTokenExpired(onTokenExpired);
    void manager.getUser().then((u) => {
      if (!active) return;
      if (u && !u.expired) onLoaded(u);
      else setStatus("signedOut");
    });
    return () => {
      active = false;
      manager.events.removeUserLoaded(onLoaded);
      manager.events.removeUserUnloaded(onUnloaded);
      manager.events.removeSilentRenewError(onRenewError);
      manager.events.removeAccessTokenExpired(onTokenExpired);
    };
  }, [manager, realm]);

  const signIn = useCallback(
    async (returnTo?: string, opts?: { fresh?: boolean }) => {
      await manager.signinRedirect({
        state: { returnTo: safeReturnTo(returnTo, realm) },
        ...(opts?.fresh ? { prompt: "login" } : {}),
      });
    },
    [manager, realm],
  );

  const signOut = useCallback(async () => {
    setSignedInMarker(realm, false);
    const u = await manager.getUser();
    // Ends the Keycloak session too, so every tab and app signed in through it is signed out (WEB-F-SH-01).
    await manager.signoutRedirect(u?.id_token ? { id_token_hint: u.id_token } : undefined);
  }, [manager, realm]);

  const completeSignIn = useCallback(async () => {
    const u = await manager.signinRedirectCallback();
    setSignedInMarker(realm, true);
    setUser(u);
    setStatus("signedIn");
    const state = u.state as { returnTo?: unknown } | undefined;
    return safeReturnTo(state?.returnTo, realm);
  }, [manager, realm]);

  const value = useMemo<AuthState>(
    () => ({ realm, status, user, signIn, signOut, completeSignIn }),
    [realm, status, user, signIn, signOut, completeSignIn],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
